import React from "react";
import { useMappedState } from "redux-react-hook";
import { toast } from "react-toastify";
import _ from "lodash";

import { Table } from "src/library/components/Tables";
import {
  IPaymentCard,
  usePaymentApi
} from "src/modules/payment/api/payment.api";
import {
  FilterParams,
  ITerminology,
  PaginatedParams
} from "src/library/utils";
import { ITransaction } from "src/modules/payment/api/transactions.api";
import { ITableInstance } from "src/library/components/Tables/Table";
import { paymentCardDropTableColumns } from "src/core/shared/components/tables";
import { Button } from "src/library/components/Button";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { sentenceCase } from "src/library/utils/string-manipulations";
import {
  IDrop,
  useDropsApi
} from "src/core/drops/api/drops.api";
import { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";

import { QuickAssignCurrentDrop } from "../QuickAssignCurrentDrop/QuickAssignCurrentDrop";
import { PaymentMethodBranchAssignmentSearch } from "../PaymentMethodBranchAssignmentSearch";
import { RemoveBranchModal } from "../RemoveBranchModal";

export interface AssignedDropsTabProps {
  paymentMethod: IPaymentCard;
  terminology: ITerminology;
}

/**
 * Renders drops that are assigned to a payment method.
 * Allows ability to assign and remove drop associations from or to the payment method.
 */
const AssignedDropsTab: React.FunctionComponent<AssignedDropsTabProps> = ({ paymentMethod, terminology }) => {
  const TableRef = React.useRef<ITableInstance<ITransaction>>(null);
  const [ removeBranchModal, setRemoveBranchModal ] = React.useState<IDrop | null>(null);
  const [ listPaymentCardBranchesRes, listPaymentCardBranchesReq ] = usePaymentApi("GET_PAYMENT_METHOD_BRANCHES");
  const [ assignPaymentCardBranchRes, assignPaymentCardBranchReq ] = usePaymentApi("ASSIGN_BRANCHES_TO_PAYMENT_METHOD");
  const [ listUserBranchesRes, listUserBranchesReq ] = useDropsApi("GET_USER_DROPS");
  const { user } = useMappedState(({ global }: IStore) => ({ user: global.user }));
  const [ { drop }, { checkDropIsSynced } ] = useDrop();

  const handleBranchSearchSelection = React.useCallback((branch: IDrop) => {
    assignPaymentCardBranchReq({
      paymentMethodId: paymentMethod.id,
      branchIds: [ branch.id ]
    });
  }, [ assignPaymentCardBranchReq, paymentMethod.id ]);

  // search for assigning branch to card
  const handleBranchSearchFetch = React.useCallback((searchTerm: string) => {
    if (user) {
      listUserBranchesReq({
        userId: user.id,
        filters: {
          $or: [ { name: { $like: `%${searchTerm}%` } }, { branchNumber: { $like: `%${searchTerm}%` } } ],
          status: "active"
        },
        pagination: {
          offset: 0,
          page: 0,
          limit: 20,
          sort: [ [ "name", "ASC" ] ]
        }
      });
    }
  }, [ listUserBranchesReq, user ]);

  // filter for table data
  const onFetchPaymentCardBranchData = React.useCallback((filters: FilterParams<ITransaction>, pagination: PaginatedParams<ITransaction>) => {
    if (drop) {
      listPaymentCardBranchesReq({
        paymentMethodId: paymentMethod.id,
        params: {
          customerId: drop.customerId,
          filters,
          pagination
        }
      });
    }
  }, [
    drop,
    listPaymentCardBranchesReq,
    paymentMethod.id
  ]);

  // when removing a assigned branch, handle the sucecss
  const handleOnRemoveSuccess = React.useCallback(() => {
    // reload table to get up-to-date data
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }

    // close modal
    setRemoveBranchModal(null);
  }, []);

  // On assignPaymentCardBranchRes change
  React.useEffect(() => {
    if (assignPaymentCardBranchRes.error) {
      toast.error(assignPaymentCardBranchRes.error.message);
    }

    if (assignPaymentCardBranchRes.data && drop) {
      toast.success(`The selected ${terminology.drop.singular.text} has been successfully assigned to this card.`);

      // refresh the table data
      if (TableRef.current) {
        TableRef.current.reloadTable();
      }

      // update the current drop in context if it matches the criteria below
      if (drop && _.find(assignPaymentCardBranchRes.data, { branchId: drop.id })) {
        checkDropIsSynced();
      }
    }
  }, [
    assignPaymentCardBranchRes.data,
    assignPaymentCardBranchRes.error,
    checkDropIsSynced,
    drop,
    paymentMethod.id,
    terminology.drop.singular.text
  ]);

  return (
    <>
      {(!listPaymentCardBranchesRes.pending && !listPaymentCardBranchesRes.data?.count) && (
        <ErrorNotice
          message={`This card is not assigned to any ${terminology.drop.plural.text} yet.`}
          type="warn"
        />
      )}

      <QuickAssignCurrentDrop
        assignToCard={handleBranchSearchSelection}
        loading={assignPaymentCardBranchRes.pending || listPaymentCardBranchesRes.pending}
      />

      <PaymentMethodBranchAssignmentSearch
        loading={listUserBranchesRes.pending}
        label={`Search for ${terminology.drop.plural.text} to assign to this payment method`}
        items={listUserBranchesRes.data?.result || []}
        onFetch={handleBranchSearchFetch}
        onSelected={handleBranchSearchSelection}
      />

      <Table
        ref={TableRef}
        tableId="drops"
        loading={listPaymentCardBranchesRes.pending}
        tableOf={sentenceCase(terminology.drop.plural.text)}
        view="list"
        viewAllowedToToggle
        data={listPaymentCardBranchesRes.data ? listPaymentCardBranchesRes.data.result : []}
        error={listPaymentCardBranchesRes.error}
        onFetchData={onFetchPaymentCardBranchData}
        defaultPagination={{
          page: 0,
          limit: 10,
          offset: 0,
          sort: [ [ "id", "DESC" ] ]
        }}
        pages={listPaymentCardBranchesRes.data ? listPaymentCardBranchesRes.data.pages : 0}
        totalRows={listPaymentCardBranchesRes.data ? listPaymentCardBranchesRes.data.count : 0}
        columns={paymentCardDropTableColumns({
          Button: row => (
            <Button
              type="button"
              onClick={() => setRemoveBranchModal(row.original as IDrop)}
            >
              Remove
            </Button>
          )
        })}
      />

      <RemoveBranchModal
        open={!!removeBranchModal}
        onDismiss={() => setRemoveBranchModal(null)}
        paymentMethod={paymentMethod}
        terminology={terminology}
        drop={removeBranchModal}
        onRemoveSuccess={handleOnRemoveSuccess}
      />
    </>
  );
};

export default AssignedDropsTab;